import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import * as _ from 'lodash';
import * as utils from 'common/utils';
import { FREQUENCY_UNITS, deriveSpectrumDisplay, preferredSpectrumXUnit } from 'common/utils/spectrumDisplay';
import * as helpers from 'common/components/Chart/utils/helpers';
import FlexContainer from 'common/components/atoms/FlexContainer';
import { utilColors, defaultChartSettings, vectorTagTypes } from 'common/charts/constants';
import { chartFeatureDisplayName } from 'common/charts/chartI18n';
import { getTagTemplateMetadata } from 'home/TagTemplates/actions/tagTemplates.actions';
import { toggleShowSettingInSession } from 'home/AssetHierarchy/actions/assetDetails.actions';
import * as overlayUtils from 'utils/overlayUtils';
import { getVisible as getAnnotationsVisible } from 'home/Annotations/selectors/annotations.selectors';
import ErrorBoundary from 'common/components/ErrorBoundary/ErrorBoundary';
import { CHART_TYPES } from '../../constants/assetHierarchy.constants';
import * as assetDetailsActions from '../../actions/assetDetails.actions';
import * as spectrumFeatureActions from '../../actions/spectrumFeatures.actions';
import * as userActions from '../../../../onboarding/user.actions';
import * as hierarchyUtils from '../../utils/assetHierarchyUtils';
import ChartsViewToolbar from './ChartsViewToolbar';
import ChartsViewChartArea from './ChartsViewChartArea';
import ChartsViewModals from './ChartsViewModals';
import ChartsViewOverlays from './ChartsViewOverlays';
import { paginateAndFetchChartData } from '../../helpers/helpers';
import { getChartSettingsFromSession, createChartsViewSessionUtils } from '../../helpers/chartsViewSessionUtils';
import { createChartsViewChartDataOrchestrator } from '../../helpers/chartsViewChartDataOrchestrator';
import { createChartsViewModalHandlers } from '../../helpers/chartsViewModalHandlers';
import { createChartsViewOverlayCallbacks } from '../../helpers/chartsViewOverlayCallbacks';
import { createOverlayTraceController } from '../../helpers/chartsViewOverlayTraceController';
import { measuredPeakForAlarmForm } from '../../utils/alarmParameters';
import {
  applyPointFilter,
  getSpeedFilteredOverlayPoints as getFilteredOverlayPoints
} from '../../helpers/overlayPointFilter';

const ConfigContainer = styled.div`
  ${props => props.background && `background: ${props.background};`}
  overflow: auto;
  height: 100%;
`;

class ChartsView extends Component {
  constructor(props) {
    super(props);
    this.toggleCalendarExpand = this.toggleCalendarExpand.bind(this);
    this.mouseoverChart = this.mouseoverChart.bind(this);
    this.setInitialSelection = this.setInitialSelection.bind(this);
    this.getLabelAndUnits = this.getLabelAndUnits.bind(this);
    this.getWaveAndSpecForId = this.getWaveAndSpecForId.bind(this);

    const chartSettings = getChartSettingsFromSession();
    this._isMounted = true;
    this._speedFilterCacheRef = {
      speedFilterCache: null,
      lastFilterCriteria: null,
      lastSelectedPointsRef: null
    };
    this.state = {
      showCharts: true,
      brushSelected: null,
      amp_type: '',
      feature: '',
      config: {},
      tag: {},
      measurement_type: 1,
      chartType: '',
      addingNote: false,
      editTrend: false,
      envelopeAlarm: false,
      selectedRange: undefined,
      days: 30,
      calendarExpanded: false,
      customDayFrom: '',
      customDayTo: '',
      number_of_spectrums: 10,
      waterfallXUnit: 'Hz',
      filterMin: '',
      filterMax: '',
      selection: {},
      bin_nos: [],
      componentNode: null,
      showBaseline: !!getChartSettingsFromSession().showBaseline,
      loadingWaterfall: false,
      logScale: true,
      paginationLoading: false,
      chartLoading: false,
      showOverlay: !!chartSettings.showOverlay,
      selectedPoints: [],
      // Requested spectrum display units. `null` means "no explicit request":
      // the unit then resolves to the backend unit, which is already in the
      // user's preferred frequency unit. The spectrum_x_scale preference seeds
      // an Orders request here; if shaft speed is unavailable the resolver
      // falls back to the backend unit until Orders becomes displayable.
      // All conversion is derived from pristine data in ChartItem/spectrumDisplay.
      spectrumYUnit: null,
      spectrumXUnit: preferredSpectrumXUnit(props.currentUser),
      showOverlayNote: !!chartSettings.showOverlay && localStorage.getItem('overlayInstructionMessageDismissed') !== 'true',
      // Performance caching for filtered points
      filteredPointsCache: null,
      lastFilterCriteria: null,
      lastSelectedPoints: null
    };

    this.overlayController = createOverlayTraceController({
      getState: () => this.state,
      setState: (updater, callback) => this.setState(updater, callback),
      getProps: () => this.props,
      isMounted: () => this._isMounted,
      getShaftSpeed: () => this.getShaftSpeed()
    });

    this.sessionUtils = createChartsViewSessionUtils({
      getState: () => this.state,
      getProps: () => this.props
    });

    const orchestratorRef = {};
    this.overlayCallbacks = createChartsViewOverlayCallbacks({
      getState: () => this.state,
      setState: (updater, callback) => this.setState(updater, callback),
      getProps: () => this.props,
      getShaftSpeed: () => this.getShaftSpeed(),
      setChartSettingsInSession: () => this.sessionUtils.setChartSettingsInSession(),
      fetchOverlaySpectrumForPoint: (...args) => this.overlayController.fetchOverlaySpectrumForPoint(...args),
      fetchOverlayWaveformForPoint: (...args) => this.overlayController.fetchOverlayWaveformForPoint(...args),
      ensureOverlayTraceDataForChartType: (...args) => this.overlayController.ensureOverlayTraceDataForChartType(...args),
      updatePreviewConfig: (...args) => orchestratorRef.updatePreviewConfig(...args),
      getSpectrumMarkers: (...args) => this.getSpectrumMarkers(...args),
      getFilterCriteria: () => this.getFilterCriteria()
    });

    this.chartDataOrchestrator = createChartsViewChartDataOrchestrator({
      getState: () => this.state,
      setState: (updater, callback) => this.setState(updater, callback),
      getProps: () => this.props,
      getParamsForChartsData: () => this.getParamsForChartsData(),
      getWaveformAndSpectrum: (...args) => this.overlayCallbacks.getWaveformAndSpectrum(...args),
      setSelection: data => this.setSelection(data),
      getDataFromTimestamp: dataset => this.getDataFromTimestamp(dataset),
      refreshOverlayTraces: (...args) => this.overlayController.refreshOverlayTraces(...args),
      refreshOverlayPointsWithNewFeature: (...args) => this.overlayController.refreshOverlayPointsWithNewFeature(...args),
      updateSelectedPoints: (...args) => this.overlayCallbacks.updateSelectedPoints(...args),
      setChartSettingsInSession: () => this.sessionUtils.setChartSettingsInSession(),
      setBinNosInSession: () => this.sessionUtils.setBinNosInSession(),
      setCalendarInSession: () => this.sessionUtils.setCalendarInSession(),
      setTimeStampInSession: timestamp => this.sessionUtils.setTimeStampInSession(timestamp),
      getSpectrumMarkers: (...args) => this.getSpectrumMarkers(...args)
    });
    Object.assign(orchestratorRef, this.chartDataOrchestrator);

    this.modalHandlers = createChartsViewModalHandlers({
      getState: () => this.state,
      setState: (updater, callback) => this.setState(updater, callback),
      getProps: () => this.props,
      calendarSelect: (...args) => this.chartDataOrchestrator.calendarSelect(...args),
      getChartData: (...args) => this.chartDataOrchestrator.getChartData(...args),
      setChartType: (...args) => this.overlayCallbacks.setChartType(...args)
    });

    this.getChartSettingsFromSession = this.sessionUtils.getChartSettingsFromSession;
    this.setChartSettingsInSession = this.sessionUtils.setChartSettingsInSession;
    this.setCalendarInSession = this.sessionUtils.setCalendarInSession;
    this.setBinNosInSession = this.sessionUtils.setBinNosInSession;
    this.setTimeStampInSession = this.sessionUtils.setTimeStampInSession;
    this.updateUrlQueryString = this.sessionUtils.updateUrlQueryString;
    this.getChartSettingsFromUrl = this.sessionUtils.getChartSettingsFromUrl;
    this.setSessionStorageFromUrl = this.sessionUtils.setSessionStorageFromUrl;

    this.fetchChartData = this.chartDataOrchestrator.fetchChartData;
    this.getMeasurementData = this.chartDataOrchestrator.getMeasurementData;
    this.calendarSelect = this.chartDataOrchestrator.calendarSelect;
    this.refreshChart = this.chartDataOrchestrator.refreshChart;
    this.getChartData = this.chartDataOrchestrator.getChartData;

    this.openAddEditFeatures = this.modalHandlers.openAddEditFeatures;
    this.closeAddEditFeature = this.modalHandlers.closeAddEditFeature;
    this.edit = this.modalHandlers.edit;
    this.openEnvelopeAlarm = this.modalHandlers.openEnvelopeAlarm;
    this.closeEnvelopeAlarm = this.modalHandlers.closeEnvelopeAlarm;
    this.openEditTrend = this.modalHandlers.openEditTrend;
    this.openEditTagInfo = this.modalHandlers.openEditTagInfo;
    this.openRULSettings = this.modalHandlers.openRULSettings;
    this.closeRULSettings = this.modalHandlers.closeRULSettings;
    this.closeEditTrend = this.modalHandlers.closeEditTrend;
    this.onFeatureDetailsClick = this.modalHandlers.onFeatureDetailsClick;
    this.closeFeatureDetailsClick = this.modalHandlers.closeFeatureDetailsClick;
    this.toggleOverallAlarm = this.modalHandlers.toggleOverallAlarm;
    this.toggleAddingNote = this.modalHandlers.toggleAddingNote;
    this.openEditDemod = this.modalHandlers.openEditDemod;
    this.closeEditDemod = this.modalHandlers.closeEditDemod;
    this.openBaselinePage = this.modalHandlers.openBaselinePage;

    this.getWaveformAndSpectrum = this.overlayCallbacks.getWaveformAndSpectrum;
    this.debouncedGetWaveformAndSpectrum = this.overlayCallbacks.debouncedGetWaveformAndSpectrum;
    this.toggleSpectrumYUnit = this.overlayCallbacks.toggleSpectrumYUnit;
    this.setChartType = this.overlayCallbacks.setChartType;
    this.isOverlayMessageDismissed = this.overlayCallbacks.isOverlayMessageDismissed;
    this.handleOverlayMessageDismiss = this.overlayCallbacks.handleOverlayMessageDismiss;
    this.toggleShowOverlay = this.overlayCallbacks.toggleShowOverlay;
    this.updateSelectedPoints = this.overlayCallbacks.updateSelectedPoints;
  }

  componentDidMount = () => {
    // updates the calendar from url timestamp
    this.setSessionStorageFromUrl();

    this.updateCalendar();
    this.props.getTagTemplateMetadata();
    const { currentAccount } = this.props;
    if (_.isEmpty(currentAccount)) {
      this.props.userActions.getCurrentAccount().catch(() => {});
    }
    this.updateBinNos();
    const config = this.props.previewConfig ? this.props.previewConfig : this.getConfigFromHierarchy(true);
    if (_.isEmpty(config)) return;
    this.setState({
      config,
      componentNode: hierarchyUtils.getComponentNodeByConfigId(this.props.assetHierarchy.hierarchy, config.id)
    });
    const tag = this.props.previewTag ? this.props.previewTag : this.getTagFromHierarchy();
    if (_.isEmpty(tag)) return;
    this.setState({ tag });
  }

  resetState = () => {
    this.setState({
      addingNote: false,
      showCharts: true,
      editTrend: false,
      editTagInfo: false,
      editDemod: false,
      editRULSettings: false,
      openFeature: false,
      openFeatureDetails: false,
      openOverallAlarm: false,
      envelopeAlarm: false,
      loadingWaterfall: false,
      tag: {},
      selectedPoints: []
    });
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.alarmFormChangesPeak(prevState)) this.refreshChart();
    if (
      prevProps.currentUser.default_bandwidth_mode !==
      this.props.currentUser.default_bandwidth_mode
    ) {
      const measurement_type = this.getDefaultMeasurementTypeFromPreference();
      if (measurement_type !== this.state.measurement_type) {
        this.setState({ measurement_type }, () => this.setChartSettingsInSession());
      }
    }

    const urlParams = new URLSearchParams(this.props.location.search);
    const settings = this.props.searchParams ? this.props.searchParams : Object.fromEntries(urlParams);

    if (this.props.match.params.config_id !== prevProps.match.params.config_id) {
      this.resetState();
    }

    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({ selectedPoints: [] });
    }

    if (!_.isEqual(prevProps.breadcrumb.machine, this.props.breadcrumb.machine)) {
      // updates the bin_nos from url once machine_id is availbale
      this.setSessionStorageFromUrl();

      this.updateBinNos();
    }

    if (!this.props.previewConfig) {
      const config = this.getConfigFromHierarchy(this.props.match.params.config_id !== prevProps.match.params.config_id);
      if (_.isEmpty(config)) return;
      if (!_.isEqual(this.state.config, config)) {
        if (this.props.updateConfig) {
          this.props.updateConfig(config);
        }
        this.setState({
          config,
          brushSelected: null,
          componentNode: hierarchyUtils.getComponentNodeByConfigId(this.props.assetHierarchy.hierarchy, config.id)
        });
      }
    }

    if (!_.isEqual(prevState.chartType, this.state.chartType)) {
      this.setState({ brushSelected: null, addingNote: false });
    }

    if (!_.isEmpty(this.state.config) &&
      this.state.config.default &&
      !_.isEmpty(this.state.tag) &&
      prevState.tag.structure_type !== this.state.tag.structure_type
    ) {
      this.updateChartSettings();
    }

    const tag = this.props.previewTag ? this.props.previewTag : this.getTagFromHierarchy();
    if (_.isEmpty(tag)) {
      if (this.state.config && this.state.config.default) return;
    }
    if (!_.isEqual(this.state.tag, tag)) {
      const prevTagId = this.state.tag && this.state.tag.id;
      const newTagId = tag && tag.id;
      const tagChanged = prevTagId !== newTagId;
      if (tagChanged) {
        this.bumpOverlayEpoch();
        this._speedFilterCacheRef.speedFilterCache = null;
      }
      const tagStateUpdate = tagChanged ? { selectedPoints: [] } : {};
      const amp_type = helpers.ampTypeOnTagChange(this.state.tag, tag, this.state.amp_type);
      if (amp_type !== this.state.amp_type) {
        this.setState({ tag, amp_type, ...tagStateUpdate }, this.updateUrlQueryString);
      } else {
        this.setState({ tag, ...tagStateUpdate });
      }
      this.updateChartTypeIfRequired();
      if (this.props.machine_id ? this.props.machine_id : this.props.breadcrumb.machine.id) {
        this.setSessionStorageFromUrl(tag.structure_type);
      }
      if (this.state.feature && ['vibration', 'current'].includes(tag.type) &&
        ![...tag.features, ...tag.spectrum_features].includes(this.state.feature)) {
        this.setState({ feature: 'rms' });
      }
    }
    if ((!this.state.config.trends_data && this.state.config.config_id && !this.loading) ||
      (!_.isEqual(this.state.config.trends_config, prevState.config.trends_config))) {
      this.resetState();
      const params = this.getParamsForChartsData();
      this.loading = true;
      params.amp_type = helpers.resolveAmpTypeForTag(tag, params.amp_type);
      if (tag.type !== 'vibration') delete params.amp_type;
      if (tag.type !== 'vibration' && tag.type !== 'current') delete params.signal_type;

      if (this.props.previewConfig) {
        params.amp_type = settings.amp_type;
        if (params.amp_type !== 'demod') delete params.signal_type;
        if (settings.amp_type === 'demod') {
          params.signal_type = 'demod';
          params.amp_type = 'acceleration';
        }
      }

      this.setState({ paginationLoading: true, chartLoading: true });
      paginateAndFetchChartData(params, (res) => {
        if (!res) return;
        const tag_id = res[0].tag_id;
        const trendData = res[0].trend_data;
        if (trendData.length > 0 && this.state.tag.structure_type === 'vector') {
          const data = this.getDataFromTimestamp(trendData);
          const timestampFromData = data ? data.x : trendData[trendData.length - 1].x;
          const timestamp = this.props.previewConfig ? settings.timestamp : timestampFromData;
          const params = {
            tag_id,
            timestamp
          };
          if (this.state.amp_type) params.amp_type = this.state.amp_type;
          if (this.state.feature) params.feature = this.state.feature;
          this.getMeasurementData(tag_id, params);
        }

        this.loading = false;
        this.setInitialSelection();
      }, [tag], this.fetchChartData).then(
        () => this.setState({ paginationLoading: false, chartLoading: false })
      );
    }

    if (this.state.config && this.state.config.default && this.state.chartType === 'waterfall_spectrum') {
      this.fetchWaterfallDataIfRequired(prevState);
    }
    if (this.state.config && !this.state.config.default && !_.isEmpty(this.state.tag)) this.setState({ tag: {} });

    // Check if overlay state changed in session storage
    const chartSettings = getChartSettingsFromSession();
    if (chartSettings.showOverlay !== this.state.showOverlay) {
      this.setState({
        showOverlay: chartSettings.showOverlay,
        showOverlayNote: chartSettings.showOverlay && localStorage.getItem('overlayInstructionMessageDismissed') !== 'true'
      });
    }
  }

  componentWillUnmount = () => {
    this._isMounted = false;
    this.bumpOverlayEpoch();
    if (this.state.selectedPoints.length > 0) {
      const chartSettings = this.getChartSettingsFromSession();
      if (chartSettings.showOverlay) {
        chartSettings.showOverlay = false;
        sessionStorage.setItem('chartSettings', JSON.stringify(chartSettings));
      }
    }
  }

  bumpOverlayEpoch = () => this.overlayController.bumpOverlayEpoch();

  isOverlayOperationCurrent = (...args) => this.overlayController.isOverlayOperationCurrent(...args);

  logOverlayFetchError = (...args) => this.overlayController.logOverlayFetchError(...args);

  retryOverlayTraceForPoint = (...args) => this.overlayController.retryOverlayTraceForPoint(...args);

  updateBinNos = () => {
    const chartSettings = this.getChartSettingsFromSession();
    const machine_id = this.props.machine_id ? this.props.machine_id : this.props.breadcrumb.machine.id;
    if (chartSettings && chartSettings.bin_nos && chartSettings.bin_nos[machine_id]) {
      const bin_nos = chartSettings.bin_nos[machine_id];
      if (this.state.bin_nos !== bin_nos) this.setState({ bin_nos });
    } else {
      this.setState({ bin_nos: [] });
    }
  }

  updateChartTypeIfRequired = () => {
    const shownChartTypes = CHART_TYPES.filter(t => !this.isHidden(t));
    if (!shownChartTypes.includes(this.state.chartType)) {
      this.setState({ chartType: shownChartTypes[0] });
    }
  }

  updateChartSettings = () => {
    const { config, tag } = this.state;
    const { structure_type } = tag;
    const chartSettings = this.getChartSettingsFromSession();
    const urlSettings = this.getChartSettingsFromUrl(structure_type);
    const preferredMeasurementType = this.getDefaultMeasurementTypeFromPreference();
    let settings;
    if (urlSettings && urlSettings[structure_type]) {
      settings = {
        ...urlSettings[structure_type],
        measurement_type: preferredMeasurementType
      };
    } else if (chartSettings && chartSettings[structure_type]) {
      settings = {
        ...chartSettings[structure_type],
        measurement_type: preferredMeasurementType
      };
    } else {
      settings = {
        ...defaultChartSettings[structure_type],
        measurement_type: preferredMeasurementType,
        ...(config.trends_config[0].amp_type && { amp_type: config.trends_config[0].amp_type }),
        ...(config.trends_config[0].feature && { feature: config.trends_config[0].feature })
      };
    }
    // Vibration and magnetic flux share the saved 'vector' settings, so the
    // amplitude saved there can belong to another tag type - an empty one from
    // magnetic flux made the chart request amp_type= and fail.
    const savedAmpType = settings.amp_type !== undefined ? settings.amp_type : this.state.amp_type;
    settings.amp_type = helpers.resolveAmpTypeForTag(tag, savedAmpType);
    this.setState(settings, () => this.setChartSettingsInSession());
  }

  getDefaultMeasurementTypeFromPreference = () => {
    const { currentUser } = this.props;
    return currentUser && currentUser.default_bandwidth_mode === 'high_resolution' ? 2 : 1;
  }

  updateCalendar = () => {
    const chartSettings = this.getChartSettingsFromSession();
    if (chartSettings && chartSettings.calendar) {
      const calendarSettings = chartSettings.calendar;
      if (calendarSettings.customDayFrom) calendarSettings.customDayFrom = new Date(calendarSettings.customDayFrom);
      if (calendarSettings.customDayTo) calendarSettings.customDayTo = new Date(calendarSettings.customDayTo);
      if (calendarSettings.days === null) calendarSettings.days = undefined;

      this.setState({
        ...chartSettings.calendar
      });
    }
  }

  fetchWaterfallDataIfRequired = (prevState) => {
    if (this.state.loadingWaterfall) return;
    const { tag, config, amp_type } = this.state;
    if (!tag || _.isEmpty(tag)) return;

    const params = {
      number_of_spectrums: this.state.number_of_spectrums,
      show_baseline: this.state.showBaseline
    };
    if (amp_type === 'demod') params.type = 'demod';
    if (amp_type) params.amp_type = amp_type === 'demod' ? 'acceleration' : amp_type;
    else params.amp_type = this.getAmpTypeAndFeature().amp_type;
    if (this.state.days !== 'custom' && this.state.days !== undefined) {
      params.days = this.state.days;
    } else if (this.state.days === 'custom') {
      params.from_timestamp = this.state.customDayFrom;
      params.to_timestamp = this.state.customDayTo;
    }
    if (this.state.measurement_type) params.measurement_type = this.state.measurement_type;
    if (!_.isEmpty(this.state.bin_nos)) params.bin_nos = this.state.bin_nos;
    if (
      !this.state.config.waterfall_spectrum ||
      (prevState.config && prevState.config.trends_config !== this.state.config.trends_config) ||
      prevState.customDayFrom !== this.state.customDayFrom ||
      prevState.customDayTo !== this.state.customDayTo ||
      prevState.days !== this.state.days ||
      prevState.number_of_spectrums !== this.state.number_of_spectrums ||
      prevState.amp_type !== this.state.amp_type ||
      prevState.measurement_type !== this.state.measurement_type ||
      (prevState.chartType !== this.state.chartType) ||
      prevState.tag.bandpass_lower_cutoff !== this.state.tag.bandpass_lower_cutoff ||
      prevState.tag.bandpass_upper_cutoff !== this.state.tag.bandpass_upper_cutoff ||
      prevState.bin_nos !== this.state.bin_nos ||
      prevState.showBaseline !== this.state.showBaseline
    ) {
      this.setState({ loadingWaterfall: true });
      if (this.props.previewConfig) {
        this.setState({ paginationLoading: true });
      }
      this.props.assetDetailsActions.getTagWaterfallSpectrum(tag.id, params, config.config_id).then(
        (res) => {
          this.setState(prevState => ({
            loadingWaterfall: false,
            paginationLoading: false,
            config: {
              ...prevState.config,
              waterfall_spectrum: res,
            },
          }));
        }
      )
        .catch((error) => {
          if (error) {
            this.setState({ loadingWaterfall: false, paginationLoading: false });
          }
        });
    }
  }

  getConfigFromHierarchy = (switchAccountIfRequired = false) => {
    const { sites } = this.props.assetHierarchy;
    const { machine_id, site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    const config_id = Number.parseInt(this.props.match.params.config_id, 10);
    const configList = hierarchyUtils.getConfigList(machine_id || this.props.machine_id, site_id, sites);
    if (!configList || configList.loading) return {};
    const config = configList.find(item => item.config_id === config_id);
    if (config) return config;
    if (this.props.previewConfig) return this.props.previewConfig;
    if (switchAccountIfRequired) this.props.assetDetailsActions.validateAndSwitchAccountOnConfig(config_id);
    return {};
  }

  getTagFromHierarchy = () => {
    if (this.props.previewTag) return this.props.previewTag;
    const { sites } = this.props.assetHierarchy;
    const { machine_id, site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    const tagsList = hierarchyUtils.getTagsList(machine_id || this.props.machine_id, site_id, sites);
    if (_.isEmpty(tagsList) || tagsList.loading ||
      !(this.state.config && this.state.config.default)) return {};
    const urlParams = new URLSearchParams(this.props.location.search);
    const urlTagId = parseInt(urlParams.get('tag_id'), 10);
    if (!Number.isNaN(urlTagId)) {
      const fromUrl = tagsList.find(item => item.id === urlTagId);
      if (fromUrl) return fromUrl;
    }
    return tagsList.find(item => item.id === this.state.config.trends_config[0].tag_id) || {};
  }

  getAmpTypeAndFeature = () => {
    const { config } = this.state;
    const chartSettings = this.getChartSettingsFromSession();
    const tag_type = config.trends_config[0].tag_type;
    const structure_type = vectorTagTypes.includes(tag_type) ? 'vector' : 'scalar';
    if (chartSettings && chartSettings[structure_type]) {
      return chartSettings[structure_type];
    }
    return {
      amp_type: config.trends_config[0].amp_type,
      feature: config.trends_config[0].feature,
      chartType: tag_type === 'current' ? 'spectrum' : 'TSW',
    };
  }

  getParamsForChartsData = () => {
    const { currentAccount } = this.props;
    const { amp_type, feature, config, tag, days, customDayFrom, customDayTo, bin_nos, logScale } = this.state;
    let chartType = _.cloneDeep(this.state.chartType);
    const params = {
      config_id: config.config_id
    };
    const chartSettings = this.getAmpTypeAndFeature();
    if (!_.isEmpty(tag)) {
      if (tag.type === 'current') params.log_scale = logScale;
    } else if (config && config.trends_config[0].tag_type === 'current') {
      params.log_scale = logScale;
    }
    if (config.default) {
      if (amp_type) params.amp_type = amp_type;
      else params.amp_type = chartSettings.amp_type;

      if (config.trends_config[0].feature) {
        if (feature) params.feature = feature;
        else params.feature = chartSettings.feature;
      }
      if (!chartType) {
        chartType = chartSettings.chartType;
        this.setState({ chartType });
      }
    }

    if (days !== 'custom' && days !== undefined) {
      params.days = days;
    } else if (days === 'custom') {
      params.from_timestamp = customDayFrom;
      params.to_timestamp = customDayTo;
    }
    if (params.amp_type === 'demod') {
      params.signal_type = 'demod';
    }

    if (['vibration', 'current'].includes(config.trends_config[0].tag_type)) {
      // Use current user preference so we never send stale state when preference
      // has just changed (setState in componentDidUpdate is async).
      params.measurement_type = this.getDefaultMeasurementTypeFromPreference();
      if (params.signal_type === 'demod') {
        if (params.measurement_type !== 1) {
          params.measurement_type = 1;
        }
        if (currentAccount.preferences && (params.feature !== 'rms' && params.feature !== 'peak')) {
          params.feature = currentAccount.preferences.chart_feature_preference;
        }
        this.setState({
          amp_type: params.amp_type,
          measurement_type: params.measurement_type,
          feature: params.feature
        }, () => this.setChartSettingsInSession());
        if (config.trends_config[0].tag_type === 'vibration') {
          params.amp_type = 'acceleration';
          if (params.amp_type === 'normal') params.amp_type = 'velocity';
        }
      }
      if (config.trends_config[0].tag_type === 'vibration' && config.default && !_.isEmpty(bin_nos)) params.bin_nos = bin_nos;
    }
    if (measuredPeakForAlarmForm({
      openOverallAlarm: this.state.openOverallAlarm, feature: params.feature, tag, account: currentAccount
    })) params.peak_preference = 'true_peak';
    return params;
  }

  // The open alarm form plots the measured peak, so refetch when it opens and when it closes.
  alarmFormChangesPeak = (prevState) => {
    const { config, tag, feature, openOverallAlarm } = this.state;
    if (prevState.openOverallAlarm === openOverallAlarm || _.isEmpty(config) || !config.trends_config) return false;
    return measuredPeakForAlarmForm({
      openOverallAlarm: true,
      feature: feature || this.getAmpTypeAndFeature().feature,
      tag,
      account: this.props.currentAccount
    });
  }

  toggleCalendarExpand() {
    this.setState(prevState => ({
      calendarExpanded: !prevState.calendarExpanded,
      selectedTabItem: ''
    }));
  }

  toggleShowTabOptions = (selectedTabItem) => {
    this.setState(prevState => ({
      selectedTabItem: selectedTabItem === prevState.selectedTabItem ? '' : selectedTabItem
    }));
  }

  getTimeRangeForAnnotations = () => {
    const { config } = this.state;

    if (!config) return null;

    // Prefer the explicit domain used by the chart, which reflects the
    // user-selected time window (calendar/brush). This keeps the list in
    // sync with the trend chart overlay which also relies on this domain.
    if (config.time_range && Array.isArray(config.time_range) && config.time_range.length === 2) {
      const normalizeToDate = (v) => {
        if (!v) return new Date(NaN);
        // Support moment-like objects with toDate()
        if (v && v.isValid && typeof v.toDate === 'function') return v.toDate();
        return new Date(v);
      };
      const start = normalizeToDate(config.time_range[0]);
      const end = normalizeToDate(config.time_range[1]);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        return { start, end };
      }
    }

    // Fallback to min/max of loaded trend points if domain is unavailable
    if (
      !config.trends_data ||
      !config.trends_data[0] ||
      !config.trends_data[0].trend_data ||
      !config.trends_data[0].trend_data.length
    ) {
      return null;
    }

    const trendData = config.trends_data[0].trend_data;
    const timePoints = trendData.map(point => new Date(point.x)).filter(d => !Number.isNaN(d.getTime()));
    if (!timePoints.length) return null;

    return {
      start: new Date(Math.min(...timePoints.map(d => d.getTime()))),
      end: new Date(Math.max(...timePoints.map(d => d.getTime())))
    };
  }

  markOverlayTracesLoading = (...args) => this.overlayController.markOverlayTracesLoading(...args);

  refreshOverlayPointsWithNewAmplitudeType = (...args) =>
    this.overlayController.refreshOverlayPointsWithNewAmplitudeType(...args);

  refreshOverlayPointsWithNewFeature = (...args) =>
    this.overlayController.refreshOverlayPointsWithNewFeature(...args);

  getShaftSpeed = () => {
    const { config, waveAndSpecSelection, selectedPoints } = this.state;

    return overlayUtils.getShaftSpeed({
      config,
      state: {
        selectedPoints,
        waveAndSpecSelection
      }
    });
  }

  getSpectrumMarkers(timestamp, config, tag) {
    if (_.isEmpty(tag) || !_.includes(['vibration', 'current'], tag.type)) return;
    if (timestamp) {
      this.props.spectrumFeatureActions.getForcingFrequencies(tag.id, timestamp);
    } else if (!tag.forcingFrequencies) {
      const trend_data = config.trends_data && config.trends_data[0] && config.trends_data[0].trend_data;
      if (!_.isEmpty(trend_data)) {
        this.props.spectrumFeatureActions.getForcingFrequencies(tag.id, trend_data[trend_data.length - 1].x);
      } else {
        this.props.spectrumFeatureActions.getForcingFrequencies(tag.id);
      }
    }
    if (!tag.spectrumFeatures) {
      this.props.spectrumFeatureActions.getSpectrumFeatures(tag.id, config.config_id);
    }
  }

  findPointIndexByTimestamp = (...args) => this.overlayController.findPointIndexByTimestamp(...args);

  getPointTraceStatus = (...args) => this.overlayController.getPointTraceStatus(...args);

  updatePointTraceStatus = (...args) => this.overlayController.updatePointTraceStatus(...args);

  buildOverlayFetchParams = (...args) => this.overlayController.buildOverlayFetchParams(...args);

  mergeSpectrumOverlayIntoPoint = (...args) => this.overlayController.mergeSpectrumOverlayIntoPoint(...args);

  fetchOverlaySpectrumForPoint = (...args) => this.overlayController.fetchOverlaySpectrumForPoint(...args);

  fetchOverlayWaveformForPoint = (...args) => this.overlayController.fetchOverlayWaveformForPoint(...args);

  ensureOverlayTraceDataForChartType = (...args) =>
    this.overlayController.ensureOverlayTraceDataForChartType(...args);

  refreshOverlayWaveformsWithNewAmplitudeType = (...args) =>
    this.overlayController.refreshOverlayWaveformsWithNewAmplitudeType(...args);

  refreshOverlayTraces = (...args) => this.overlayController.refreshOverlayTraces(...args);

  setLastHoveredChart = (type) => {
    this.setState({ lastHoveredChart: type });
  };

  setSelection = (data) => {
    this.setState({
      waveAndSpecSelection: {
        ...data,
        isDate: true,
        ...this.getLabelAndUnits(data)
      },
      selection: {
        ...data,
        isDate: true,
        ...this.getLabelAndUnits(data)
      }
    });
  }

  getDataFromTimestamp = (dataset) => {
    const chartSettings = this.getChartSettingsFromSession();
    const { machine_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    if (!chartSettings.timestamp || !chartSettings.timestamp[machine_id]) return null;
    return helpers.closestXDataPointOnDataset(dataset, new Date(chartSettings.timestamp[machine_id]), true);
  }

  setInitialSelection() {
    const { config } = this.state;
    if (
      config.trends_data &&
      !config.trends_data.loading &&
      config.trends_data[0] &&
      config.trends_data[0].trend_data &&
      config.trends_data[0].trend_data.length
    ) {
      const dataset = config.trends_data[0].trend_data;
      const data = this.getDataFromTimestamp(dataset) || dataset[dataset.length - 1];
      this.setSelection(data);
    }
  }

  getLabelAndUnits(data) {
    const { config } = this.state;
    const colors = utilColors;
    if (!config.trends_data || config.trends_data.loading || !config.trends_data[0]) {
      return {};
    }
    if (config.trends_data[0].tag_type !== 'utilization') {
      return {
        yTitle:
          chartFeatureDisplayName(this.props.t, config.trends_data[0].feature) ||
          utils.CapitalizeEachWord(config.trends_data[0].tag_type),
        yUnit: config.trends_data[0].units,
        speedUnit: config.trends_data[0].speed_units
      };
    }
    return {
      y: (data.y + 1) % 3,
      yTitle: 'Status',
      color: colors[(data.y + 1) % 3]
    };
  }

  updateWaveAndSpecSelection = (selection) => {
    this.setState(prevState => ({
      waveAndSpecSelection: {
        ...prevState.selection,
        ...selection
      }
    }));
  }

  /**
   * Set the requested spectrum X-axis (frequency) unit.
   * This only records intent — all data/feature/point conversion is derived
   * from pristine data downstream (ChartItem + common/utils/spectrumDisplay).
   */
  setSpectrumXUnit = (newXUnit) => {
    if (!newXUnit || !FREQUENCY_UNITS.includes(newXUnit)) return;
    if (newXUnit === this.state.spectrumXUnit) return;
    this.setState({ spectrumXUnit: newXUnit });
  }

  /**
   * Set the requested spectrum Y-axis (amplitude) unit.
   * Like setSpectrumXUnit, this only records intent.
   */
  setSpectrumYUnit = (newYUnit) => {
    if (!newYUnit || newYUnit === this.state.spectrumYUnit) return;
    this.setState({ spectrumYUnit: newYUnit });
  }

  getWaveAndSpecForId(selection) {
    const { tag } = this.state;
    const timestamp = selection.x;
    if (timestamp) {
      this.setTimeStampInSession(timestamp);
    }
    if (tag.structure_type === 'vector') {
      // Use the new debounced function with extracted logic
      this.debouncedGetWaveformAndSpectrum(selection);
    }
    // In all cases, update the waveAndSpecSelection state
    this.updateWaveAndSpecSelection(selection);
  }

  storeBrushSelection = (selection) => {
    this.setState({
      brushSelected: selection
    });
  };

  mouseoverChart(selection) {
    this.setState({
      selection: {
        ...selection,
        isDate: true,
        ...this.getLabelAndUnits(selection)
      }
    });
  }

  /**
   * Updates the speed filter values in component state
   * @param {number} filterMin - Minimum speed value for filtering
   * @param {number} filterMax - Maximum speed value for filtering
   */
  setSpeedFilterValues = (filterMin, filterMax) => {
    this.setState({ filterMin, filterMax });
  }

  /**
   * Gets the current filter criteria from component state
   * @returns {Object} Filter criteria object
   * @returns {number} returns.filterMin - Minimum speed filter value
   * @returns {number} returns.filterMax - Maximum speed filter value
   * @returns {Array<number>} returns.bin_nos - Array of speed bin numbers
   */
  getFilterCriteria = () => {
    const { filterMin, filterMax, bin_nos } = this.state;
    return { filterMin, filterMax, bin_nos };
  }

  /**
   * Checks if a point should be filtered out based on current filter criteria
   * @param {Object} point - The point object to check
   * @param {number} [point.speed_bin] - Speed bin number for VFD assets
   * @param {number} [point.shaft_speed] - Shaft speed value
   * @param {number} [point.speed] - Alternative speed value
   * @returns {boolean} True if point should be filtered out, false otherwise
   * @example
   * const point = { speed_bin: 3, shaft_speed: 1800 };
   * const isFiltered = this.isPointFiltered(point); // false if bin 3 is in filter
   */
  isPointFiltered = (point) => {
    const criteria = this.getFilterCriteria();
    return !applyPointFilter(point, criteria);
  }

  getSpeedFilteredOverlayPoints = () => {
    const { selectedPoints } = this.state;
    return getFilteredOverlayPoints(
      selectedPoints,
      this.getFilterCriteria(),
      this._speedFilterCacheRef
    );
  }

  handleSpectraChange = (e, data) => {
    if (this.state.number_of_spectrums !== data.value) {
      this.setState({ number_of_spectrums: data.value });
    }
  }

  toggleWaterfallXUnit = () => {
    this.setState((prevState) => {
      const newUnit = prevState.waterfallXUnit === 'Orders' ? 'Hz' : 'Orders';
      return { waterfallXUnit: newUnit };
    }, () => this.setChartSettingsInSession());
  }

  toggleShowBaseline = () => {
    this.setState(prevState => ({
      showBaseline: !prevState.showBaseline
    }));
    toggleShowSettingInSession('showBaseline');
    if (this.state.chartType === 'trend') this.refreshChart();
  }

  isComponentRULEnabled = () => {
    const { tag } = this.state;
    return tag.rul_settings && tag.rul_settings.enabled;
  }

  isHidden = (chartType) => {
    const { tag, showCharts, measurement_type } = this.state;
    switch (chartType) {
      case 'trend':
        return !tag || !showCharts;
      case 'spectrum':
      case 'waveform':
      case 'TSW':
        return !tag || tag.structure_type !== 'vector' || !showCharts || measurement_type === 0;
      case 'waterfall_spectrum':
      case 'cepstrum':
        return !tag || tag.type !== 'vibration' || !showCharts || measurement_type === 0;
      default:
        return true;
    }
  }

  downloadSpectrumChart = () => {
    // Derive data and units together so the export can never pair
    // converted units with unconverted data (or vice versa).
    const display = deriveSpectrumDisplay({
      spectrum: this.state.config && this.state.config.spectrum,
      requestedXUnit: this.state.spectrumXUnit,
      requestedYUnit: this.state.spectrumYUnit,
      tagType: this.state.tag && this.state.tag.type,
      selectedPoints: this.state.selectedPoints,
      showOverlay: this.state.showOverlay
    });
    helpers.downloadChartData({
      config: this.state.config,
      chartType: this.state.chartType,
      pointTimestamp: this.state.waveAndSpecSelection && this.state.waveAndSpecSelection.x,
      spectrumXUnit: display.xUnit,
      spectrumYUnit: display.yUnit,
      spectrumData: display.spectrumData
    });
  }

  render() {
    const {
      addingNote,
      amp_type,
      brushSelected,
      bin_nos,
      calendarExpanded,
      componentNode,
      config,
      chartType,
      days,
      editTrend,
      editDemod,
      editRULSettings,
      envelopeAlarm,
      feature,
      featureForDetails,
      featureToOpen,
      filterMax,
      filterMin,
      lastHoveredChart,
      measurement_type,
      number_of_spectrums,
      openFeature,
      openFeatureDetails,
      openOverallAlarm,
      customDayFrom,
      customDayTo,
      selectedTabItem,
      selectedRange,
      selection,
      showCharts,
      tag,
      waveAndSpecSelection,
      showBaseline,
      loadingWaterfall,
      editTagInfo,
      paginationLoading,
      showOverlay,
      spectrumXUnit,
      waterfallXUnit,
    } = this.state;

    // Get filtered overlay points for display
    const filteredOverlayPoints = this.getSpeedFilteredOverlayPoints();
    const { hierarchyViewPane } = this.props;
    const showWidth = this.props.previewConfig ? '100vw ' : `calc(100vw - ${hierarchyViewPane.currentWidth + 15}px)`; // accomodate for the gap in Footer
    let hasData = false;
    if (
      config.trends_data &&
      config.trends_data[0] &&
      config.trends_data[0].trend_data &&
      config.trends_data[0].trend_data.length > 0
    ) {
      hasData = true;
    }

    const toolbarContext = {
      t: this.props.t,
      hasData,
      showCharts,
      chartType,
      amp_type,
      config,
      tag,
      addingNote,
      editTrend,
      openFeature,
      editDemod,
      editTagInfo,
      editRULSettings,
      openFeatureDetails,
      envelopeAlarm,
      openOverallAlarm,
      feature,
      measurement_type,
      logScale: this.state.logScale,
      state: this.state,
      toggleAddingNote: this.toggleAddingNote,
      toggleCalendarExpand: this.toggleCalendarExpand,
      refreshChart: this.refreshChart,
      downloadSpectrumChart: this.downloadSpectrumChart,
      edit: this.edit,
      toggleShowTabOptions: this.toggleShowTabOptions,
      openEnvelopeAlarm: this.openEnvelopeAlarm,
      toggleOverallAlarm: this.toggleOverallAlarm,
      openEditTrend: this.openEditTrend,
      openAddEditFeatures: this.openAddEditFeatures,
      openEditDemod: this.openEditDemod,
      openBaselinePage: this.openBaselinePage,
      openEditTagInfo: this.openEditTagInfo,
      openRULSettings: this.openRULSettings,
      isComponentRULEnabled: this.isComponentRULEnabled,
      setChartType: this.setChartType,
      isHidden: this.isHidden
    };

    return (
      <ConfigContainer background="white">
        <FlexContainer backgroundColor="white" direction="column">
          <ChartsViewToolbar
            addingNote={addingNote}
            bin_nos={bin_nos}
            calendarExpanded={calendarExpanded}
            chartType={chartType}
            config={config}
            customDayFrom={customDayFrom}
            customDayTo={customDayTo}
            days={days}
            editDemod={editDemod}
            editRULSettings={editRULSettings}
            editTrend={editTrend}
            editTagInfo={editTagInfo}
            envelopeAlarm={envelopeAlarm}
            feature={feature}
            filterMax={filterMax}
            filterMin={filterMin}
            hasData={hasData}
            loadingWaterfall={loadingWaterfall}
            logScale={this.state.logScale}
            measurement_type={measurement_type}
            menuItemsContext={toolbarContext}
            number_of_spectrums={number_of_spectrums}
            openFeature={openFeature}
            openFeatureDetails={openFeatureDetails}
            openOverallAlarm={openOverallAlarm}
            selectedTabItem={selectedTabItem}
            showBaseline={showBaseline}
            showCharts={showCharts}
            showOverlay={showOverlay}
            tag={tag}
            amp_type={amp_type}
            filteredOverlayPoints={filteredOverlayPoints}
            toggleShowTabOptions={this.toggleShowTabOptions}
            calendarSelect={this.calendarSelect}
            toggleCalendarExpand={this.toggleCalendarExpand}
            handleSpectraChange={this.handleSpectraChange}
            getChartData={this.getChartData}
            setSpeedFilterValues={this.setSpeedFilterValues}
            refreshChart={this.refreshChart}
            toggleShowBaseline={this.toggleShowBaseline}
            toggleShowOverlay={this.toggleShowOverlay}
            updateSelectedPoints={this.updateSelectedPoints}
            retryOverlayTraceForPoint={this.retryOverlayTraceForPoint}
          />
          <ErrorBoundary name="charts-view-chart-area" resetKeys={[this.props.match.params.config_id]}>
          <ChartsViewChartArea
            annotationsVisible={this.props.annotationsVisible}
            chartLoading={this.state.chartLoading}
            chartType={chartType}
            config={config}
            days={days}
            tag={tag}
            customDayFrom={customDayFrom}
            customDayTo={customDayTo}
            amp_type={amp_type}
            feature={feature}
            bin_nos={bin_nos}
            selection={selection}
            waveAndSpecSelection={waveAndSpecSelection}
            mouseoverChart={this.mouseoverChart}
            refreshChartAfterOutlier={() => this.refreshChart({ afterOutlier: true })}
            selectedRange={selectedRange}
            getWaveAndSpecForId={this.getWaveAndSpecForId}
            storeBrushSelection={this.storeBrushSelection}
            brushSelected={brushSelected}
            lastHoveredChart={lastHoveredChart}
            setLastHoveredChart={this.setLastHoveredChart}
            onFeatureDetailsClick={this.onFeatureDetailsClick}
            openAddEditFeatures={this.openAddEditFeatures}
            openFeature={openFeature}
            openFeatureDetails={openFeatureDetails}
            closeFeatureDetailsClick={this.closeFeatureDetailsClick}
            featureForDetails={featureForDetails}
            featureToOpen={featureToOpen}
            filterMin={filterMin}
            filterMax={filterMax}
            openEditDemod={this.openEditDemod}
            measurement_type={measurement_type}
            componentNode={componentNode}
            showBaseline={showBaseline}
            paginationLoading={paginationLoading}
            editRULSettings={editRULSettings}
            closeRULSettings={this.closeRULSettings}
            showOverlay={showOverlay}
            filteredOverlayPoints={filteredOverlayPoints}
            updateSelectedPoints={this.updateSelectedPoints}
            retryOverlayTraceForPoint={this.retryOverlayTraceForPoint}
            spectrumXUnit={this.state.spectrumXUnit}
            spectrumYUnit={this.state.spectrumYUnit}
            setSpectrumXUnit={this.setSpectrumXUnit}
            setSpectrumYUnit={this.setSpectrumYUnit}
            toggleSpectrumYUnit={this.toggleSpectrumYUnit}
            updateConfigFeatures={this.updateConfigFeatures}
            waterfallXUnit={waterfallXUnit}
            toggleWaterfallXUnit={this.toggleWaterfallXUnit}
            showCharts={showCharts}
            loading={this.loading}
            openOverallAlarm={openOverallAlarm}
            envelopeAlarm={envelopeAlarm}
            previewConfig={this.props.previewConfig}
          />
          </ErrorBoundary>
          <ChartsViewModals
            addingNote={addingNote}
            amp_type={amp_type}
            brushSelected={brushSelected}
            chartType={chartType}
            config={config}
            feature={feature}
            machineId={this.props.machine_id ? this.props.machine_id : this.props.breadcrumb.machine.id}
            waveAndSpecSelection={waveAndSpecSelection}
            onCancelNote={this.toggleAddingNote}
            tag={tag}
            showWidth={showWidth}
            editTrend={editTrend}
            closeEditTrend={this.closeEditTrend}
            getChartData={this.getChartData}
            refreshConfigMetadata={this.refreshConfigMetadata}
            selectedTags={this.props.selectedTags}
            days={days}
            editTagInfo={editTagInfo}
            envelopeAlarm={envelopeAlarm}
            closeEnvelopeAlarm={this.closeEnvelopeAlarm}
            measurement_type={measurement_type}
            componentNode={componentNode}
            openOverallAlarm={openOverallAlarm}
            toggleOverallAlarm={this.toggleOverallAlarm}
            bin_nos={bin_nos}
            openFeatureDetails={openFeatureDetails}
            closeFeatureDetailsClick={this.closeFeatureDetailsClick}
            featureForDetails={featureForDetails}
            openFeature={openFeature}
            closeAddEditFeature={this.closeAddEditFeature}
            featureToOpen={featureToOpen}
            editDemod={editDemod}
            closeEditDemod={this.closeEditDemod}
            frequencyUnits={this.props.currentUser.frequency_units}
          />
        </FlexContainer>
        <ChartsViewOverlays
          t={this.props.t}
          annotationsVisible={this.props.annotationsVisible}
          chartType={chartType}
          showCharts={showCharts}
          getTimeRangeForAnnotations={this.getTimeRangeForAnnotations}
          tag={tag}
          config={config}
          showOverlayNote={this.state.showOverlayNote}
          handleOverlayMessageDismiss={this.handleOverlayMessageDismiss}
        />
      </ConfigContainer>
    );
  }
}

const mapStateToProps = state => ({
  currentUser: state.user.user,
  rbacPermissions: state.rbacPermissions,
  assetHierarchy: state.assetHierarchyReducer.assetInfo,
  currentAccount: state.currentAccount,
  hierarchyViewPane: state.hierarchyViewPane,
  breadcrumb: state.breadcrumb,
  annotationsVisible: getAnnotationsVisible(state)
});

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
  spectrumFeatureActions: bindActionCreators(spectrumFeatureActions, dispatch),
  userActions: bindActionCreators(userActions, dispatch),
  getTagTemplateMetadata: bindActionCreators(getTagTemplateMetadata, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(ChartsView)));
